/**
 * MILYEN ELEMBE ÁGYAZZUK BE A PAPÍRT — a fájl típusából.
 *
 * KÜLÖN, TISZTA FÜGGVÉNY, mert ez a döntés nem a megjelenítésről szól, hanem
 * arról, hogy MIT TUD A BÖNGÉSZŐ. A `packages/upload-limits`
 * `ACCEPTED_MIME_TYPES` listája hat típust enged be, és ezek közül NEM MIND
 * jeleníthető meg — a kettő két külön kérdés, és a különbségük csak akkor
 * marad látható, ha külön is kimondható.
 */

/** A `viewer/` nézője a képet és a PDF-et rajzolja; a `none` a „nem jeleníthető meg" ág. */
export type EmbedKind = 'image' | 'pdf' | 'none';

/**
 * A KÉPFORMÁTUMOK, amiket minden böngésző kirajzol.
 *
 * ⚠️ A HEIC/HEIF SZÁNDÉKOSAN HIÁNYZIK, pedig a szerver ELFOGADJA. Az Apple
 * formátumát a Safarin kívül egyik böngésző sem dekódolja: kirajzolva nem
 * hibaüzenetet ad, hanem a TÖRÖTT KÉP ikonját — a könyvelő azt
 * látná, hogy „a bizonylat elveszett", pedig megvan és letölthető.
 *
 * Ilyenkor a panel nem hazudik és nem is ürül ki: kimondja, hogy ez a
 * formátum itt nem jeleníthető meg, és ott a link. A telefonos feltöltés a
 * PWA fő útja, tehát ez NEM elméleti eset.
 */
const EMBEDDABLE_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

/**
 * A `mimeType` a szervertől jön, a feltöltéskori `Content-Type`-ból. A
 * paramétereit (`; charset=…`) levágjuk és kisbetűsítünk: a típus
 * összehasonlítása az RFC szerint nem betűérzékeny, és egy `IMAGE/JPEG`
 * feltöltés miatt nem tűnhet el a kép.
 *
 * A HIÁNYZÓ ÉRTÉKET IS ELBÍRJA, pedig a típus `string`. A mező A HÁLÓZATRÓL
 * érkezik: egy régebbi backend, egy félbement válasz vagy egy elrontott DTO
 * `undefined`-et ad, és azt a típusrendszer NEM fogja meg. Kezeletlenül ez
 * `TypeError`-t dobna RENDER KÖZBEN — vagyis a teljes munkalista fehér
 * képernyőre menne egy MELLÉKES panel miatt. A papír nem a munka kapuja
 * (lefűzni nélküle is lehet), tehát a hiánya sem állíthatja meg.
 */
export function embedKind(mimeType: string | undefined): EmbedKind {
  return embedType(mimeType) === null
    ? 'none'
    : essenceOf(mimeType) === 'application/pdf'
      ? 'pdf'
      : 'image';
}

/**
 * A BEÁGYAZHATÓ TÍPUS KANONIKUS ALAKJA — vagy `null`, ha nem beágyazható.
 *
 * A nyomtatás ezzel címkézi újra a bájtokat, mielőtt a böngésző saját
 * nézőjébe adja. Ott már nem véd a szerver `sandbox` CSP-je (a blob az admin
 * originjén nyílik meg), tehát a típus az egyetlen, ami eldönti, mit csinál
 * a böngésző a tartalommal: ellenőrzött PDF vagy kép — soha nem HTML vagy SVG,
 * akármit mondott a feltöltéskori `Content-Type`.
 */
export function embedType(mimeType: string | undefined): string | null {
  const essence = essenceOf(mimeType);
  return essence === 'application/pdf' || EMBEDDABLE_IMAGE_TYPES.has(essence) ? essence : null;
}

function essenceOf(mimeType: string | undefined): string {
  return mimeType?.split(';')[0]?.trim().toLowerCase() ?? '';
}
